// deployment_class: customer_env_allowed
/**
 * LandingDetailPopover — the light-glass popover anchored DIRECTLY UNDER a clicked
 * mark, shared by every landing schema view (lineage web · fact constellation ·
 * galaxy scatter · Venn).
 *
 * Portaled to <body> so it is positioned against the true viewport — never against
 * the modal card (which, being transformed + filtered + `overflow: hidden`, is a
 * containing block for fixed children and would clip it). It flips ABOVE when there
 * is no room below, shifts the minimum needed to stay fully on-screen, and points a
 * small tail at the mark it belongs to. The `.kh-node-pop` rule re-declares the few
 * scoped tokens it needs (teal + easings) so it renders correctly outside `.kh-con`'s
 * var scope.
 *
 * Extracted from LandingConstellation 2026-08-09 (E3: additional schema views as
 * SELECTABLE views). The markup, class names, placement maths and portal target are
 * unchanged — only the input widened from a constellation node to a view-agnostic
 * payload, so the fact / galaxy / Venn views inspect through the SAME surface rather
 * than each growing a near-copy.
 */
import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { NodeStatus } from './landingDrillData';

type CSSVars = React.CSSProperties & Record<string, string | number>;

/** Anchor rect in client coordinates — the mark the popover points at. */
export interface DetailAnchor {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** What to show. `kindLabel` is the view's own word for the mark ("Stream", "Fact",
 *  "Dimension", "Partnership", "Set") — each view names its own vocabulary. */
export interface DetailPayload {
  kindLabel: string;
  label: string;
  value?: string;
  detail?: string;
  status: NodeStatus;
}

const STATUS_LABEL: Record<string, string> = {
  done: 'Completed',
  motion: 'In motion',
  watch: 'Needs attention',
  neutral: '',
};

export function LandingDetailPopover({
  payload,
  anchor,
  onClose,
}: {
  payload: DetailPayload;
  anchor: DetailAnchor;
  onClose: () => void;
}): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{
    top: number;
    left: number;
    place: 'below' | 'above';
    arrow: number;
  } | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // offsetWidth/Height = the untransformed layout box (measured while the pre-`.in`
    // scale(.98) is applied, so getBoundingClientRect would under-report the size).
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const gap = 12;
    const m = 12;
    const nodeCenterX = anchor.left + anchor.width / 2;

    // Prefer directly UNDER the mark; flip ABOVE when there isn't room below.
    let place: 'below' | 'above' = 'below';
    let top = anchor.top + anchor.height + gap;
    if (top + h > vh - m) {
      const above = anchor.top - h - gap;
      if (above >= m) {
        place = 'above';
        top = above;
      } else {
        // Neither side fully fits — keep below and clamp on-screen.
        top = Math.max(m, Math.min(top, vh - h - m));
      }
    }

    // Centre under the mark, then shift the minimum needed to stay fully on-screen.
    let left = nodeCenterX - w / 2;
    left = Math.max(m, Math.min(left, vw - w - m));

    // The tail points at the mark's horizontal centre, clamped inside the popover.
    const arrow = Math.max(18, Math.min(w - 18, nodeCenterX - left));

    setPos({ top, left, place, arrow });
  }, [anchor]);

  const statusLabel = STATUS_LABEL[payload.status] ?? '';

  return createPortal(
    <div
      ref={ref}
      className={`kh-node-pop${pos ? ` in place-${pos.place}` : ''}`}
      role="dialog"
      aria-label={`${payload.label} detail`}
      style={
        pos
          ? ({ top: pos.top, left: pos.left, '--arrow-x': `${pos.arrow}px` } as CSSVars)
          : { top: -9999, left: -9999 }
      }
    >
      <button type="button" className="kh-node-pop-close" onClick={onClose} aria-label="Close detail">
        ✕
      </button>
      <div className="kh-node-pop-kindrow">
        <span className="kh-node-pop-kind">{payload.kindLabel}</span>
        {statusLabel && (
          <span className={`kh-node-pop-status s-${payload.status}`}>{statusLabel}</span>
        )}
      </div>
      <div className="kh-node-pop-label">
        {payload.label}
        {payload.value && <b> · {payload.value}</b>}
      </div>
      {payload.detail && <p className="kh-node-pop-detail">{payload.detail}</p>}
      <div className="kh-node-pop-verify">⛓ traces to source · verify me</div>
    </div>,
    document.body,
  );
}
